/**
 * DirectReportsLayout
 *
 * Chrome-less shell for the Direct Reports deep-link page. It mirrors the main
 * app `pages/Layout/Layout.tsx` but intentionally:
 *   - omits the top `Navbar` (no header / logos / buttons), and
 *   - starts the `Sidebar` in the COLLAPSED state by default.
 *
 * The sidebar's own toggle still works, so a user can expand it if they want.
 */
import React, { useState } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { useSelector } from 'react-redux';
import { Sidebar } from '../../../components/Sidebar';
import { RootState } from '../../../state/store';

interface DirectReportsLayoutProps {
  children: React.ReactNode;
}

export const DirectReportsLayout: React.FC<DirectReportsLayoutProps> = ({ children }) => {
  const isNonMobile = useMediaQuery('(min-width: 600px)');
  // Collapsed by default per the deep-link page requirement.
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarMinified, setIsSidebarMinified] = useState(false);
  const user = useSelector((state: RootState) => state.authentication.user);
  const isSuperAdmin = (user as any)?.role === 'superadmin';
  const drawerWidth = isSidebarOpen && isSidebarMinified ? 80 : 250;

  return (
    <Box display="flex" width="100%" height="100vh">
      <Sidebar
        setIsSidebarOpen={setIsSidebarOpen}
        isSidebarOpen={isSidebarOpen}
        isSidebarMinified={isSidebarMinified}
        setIsSidebarMinified={setIsSidebarMinified}
        drawerWidth={drawerWidth}
        isNonMobile={isNonMobile}
        isSuperAdmin={isSuperAdmin}
      />
      <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
        <Box sx={{
          pt: 2,
          pr: 2,
          pb: 2,
          pl: { xs: 1.25, sm: 2.5 },
          overflowY: 'auto',
          height: '100vh',
          width: '100%',
        }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default DirectReportsLayout;
